import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { ContactMessage } from "@/entities/ContactMessage";
import { SendEmail } from "@/integrations/Core";
import { notifyAdmin } from "@/functions/notifyAdmin";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, User, Building2, ExternalLink, MessageCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useRegion, WAREHOUSES } from "@/components/RegionContext";
import { useLanguage } from "@/components/LanguageContext";
import { trackLead } from "@/lib/pixel";

const createPageUrl = (pageName) => {
  return `/${pageName.toLowerCase().replace(/([A-Z])/g, '-$1').toLowerCase()}`;
};

export default function ContactPage() {
  const { t } = useLanguage();
  const { region, warehouse } = useRegion();
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", subject: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await ContactMessage.create({
        name: formData.name, email: formData.email, phone: formData.phone,
        subject: formData.subject, message: formData.message, status: "new"
      });
      try {
        await notifyAdmin({
          channel: "ops",
          from_name: "LCTX Contact Form",
          subject: `New B2B/Contact Message: ${formData.subject}`,
          body: `New contact form submission from LCTX website:\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone || "Not provided"}\nSubject: ${formData.subject}\nRegion: ${warehouse.id.toUpperCase()} (${warehouse.address})\n\nMessage:\n${formData.message}\n\n---\nReply directly to: ${formData.email}`
        });
      } catch (emailError) { console.error("Email sending failed:", emailError); }

      try {
        await SendEmail({
          from_name: "LCTX",
          to: formData.email,
          subject: "We received your message - LCTX",
          body: `Hi ${formData.name},\n\nThank you for contacting LCTX! We've received your message and will get back to you within 24 hours.\n\nYour message:\nSubject: ${formData.subject}\n${formData.message}\n\nFor urgent matters, contact our local ${warehouse.id.toUpperCase()} warehouse:\n📞 ${warehouse.phone}\n✉️ ${warehouse.email}\n💬 WhatsApp: https://wa.me/message/7TRHEPKMGBN4B1\n\nBest regards,\nLCTX Team`
        });
      } catch (emailError) { console.error("Confirmation email failed:", emailError); }

      // Meta conversion signal: contact/B2B enquiry submitted successfully.
      trackLead({ content_category: "contact_form" });

      setIsSuccess(true);
      setIsSubmitting(false);
      setTimeout(() => {
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
        setIsSuccess(false);
      }, 5000);
    } catch (err) {
      console.error("Form submission error:", err);
      setError("Failed to send message. Please try again or contact your local JDL warehouse directly.");
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const warehouses = [WAREHOUSES.uae, WAREHOUSES.sa];

  return (
    <div className="bg-white min-h-screen">
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <h1 className="text-6xl font-black mb-6">{t("contact_hero")}</h1>
            <p className="text-2xl text-gray-300 max-w-3xl mx-auto">{t("contact_sub")}</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Warehouse cards — Dubai & Riyadh (JDL) */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {warehouses.map((wh) => {
            const isCurrent = wh.id === region;
            return (
              <motion.div
                key={wh.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card className={`h-full border-0 shadow-xl ${isCurrent ? "ring-4 ring-red-500" : ""}`}>
                  <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white p-5">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-xl font-black flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        {wh.id.toUpperCase() === "UAE" ? "Dubai · UAE" : "Riyadh · KSA"}
                      </CardTitle>
                      {isCurrent && (
                        <span className="bg-white text-red-700 text-[10px] font-black px-2 py-1 rounded-full">
                          YOUR WAREHOUSE
                        </span>
                      )}
                    </div>
                    <p className="text-red-100 text-xs mt-1">Jingdong Logistics (JDL)</p>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{t("contact_code")}</div>
                        <div className="text-gray-600 text-sm break-words">{wh.code}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900 text-sm">{t("contact_warehouse")}</div>
                        <p className="text-gray-600 text-sm">{wh.address}</p>
                        <a href={wh.googleLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-semibold mt-1">
                          {t("contact_view_map")} <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{t("contact_phone")}</div>
                        <a href={`tel:${wh.phone.replace(/\s/g, '')}`} className="text-gray-600 hover:text-red-600 text-sm">{wh.phone}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-4 h-4 text-yellow-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900 text-sm">{t("contact_email")}</div>
                        <a href={`mailto:${wh.email}`} className="text-gray-600 hover:text-red-600 text-sm break-all">{wh.email}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{t("contact_hours")}</div>
                        <p className="text-gray-600 text-sm">{wh.hours}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <Card className="border-0 shadow-2xl">
                <CardHeader className="bg-gradient-to-r from-gray-900 to-black text-white p-8">
                  <CardTitle className="text-3xl font-black">{t("contact_form_title")}</CardTitle>
                  <p className="text-gray-300 mt-2">{t("contact_form_sub")}</p>
                </CardHeader>
                <CardContent className="p-8">
                  {isSuccess ? (
                    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-12 h-12 text-green-600" />
                      </div>
                      <h3 className="text-3xl font-black text-gray-900 mb-4">{t("contact_message_sent")}</h3>
                      <p className="text-lg text-gray-600 mb-4">{t("contact_message_sent_desc")}</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {error && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-red-800">{error}</div>
                      )}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-lg font-bold">{t("contact_name")} *</Label>
                          <Input id="name" value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} placeholder="John Doe" required className="h-12 text-lg" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-lg font-bold">{t("contact_email")} *</Label>
                          <Input id="email" type="email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} placeholder="john@example.com" required className="h-12 text-lg" />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-lg font-bold">{t("contact_phone")}</Label>
                          <Input id="phone" type="tel" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} placeholder="+971 5X XXX XXXX" className="h-12 text-lg" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject" className="text-lg font-bold">{t("contact_subject")} *</Label>
                          <Input id="subject" value={formData.subject} onChange={(e) => handleInputChange("subject", e.target.value)} placeholder="How can we help?" required className="h-12 text-lg" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-lg font-bold">{t("contact_message")} *</Label>
                        <Textarea id="message" value={formData.message} onChange={(e) => handleInputChange("message", e.target.value)} placeholder="Tell us more about your inquiry..." required className="h-40 text-lg" />
                      </div>
                      <Button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 h-14 text-xl font-bold shadow-xl">
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2"><Send className="w-5 h-5" />{t("contact_send")}</span>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Quick Support */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-0 shadow-xl border-2 border-red-200 bg-red-50">
              <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4">
                <CardTitle className="text-lg font-black">{t("nav_product")}</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-700 mb-4 text-sm">
                  LCTX sells only the Air-Powered Portable Tire Changer in {warehouse.id.toUpperCase()} and KSA. Spare parts are currently unavailable.
                </p>
                <Link to={createPageUrl("Product")}>
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white h-11 text-sm font-bold">
                    {t("buy_now")} →
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="font-black text-xl text-gray-900 mb-4">{t("nav_b2b")}</h3>
                <p className="text-gray-600 text-sm mb-4">Distributor, reseller, or fleet enquiries? Submit a B2B enquiry.</p>
                <Link to={createPageUrl("Affiliate")}>
                  <Button variant="outline" className="w-full h-11 text-sm font-bold border-2">
                    {t("nav_b2b")} →
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* WhatsApp Quick Chat */}
            <a
              href="https://wa.me/8618218291826"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-green-500 hover:bg-green-600 text-white h-12 text-sm font-bold shadow-xl">
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat on WhatsApp
              </Button>
            </a>
          </div>
        </div>

        {/* Google Maps — both warehouses */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-16">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6 text-center">Our Warehouses on the Map</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {warehouses.map((wh) => {
              const isCurrent = wh.id === region;
              return (
                <Card key={wh.id} className={`border-0 shadow-2xl overflow-hidden ${isCurrent ? "ring-4 ring-red-500" : ""}`}>
                  <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-2xl font-black flex items-center gap-2">
                        <MapPin className="w-6 h-6" />
                        {wh.id.toUpperCase() === "UAE" ? "Dubai · UAE" : "Riyadh · KSA"}
                      </CardTitle>
                      {isCurrent && (
                        <span className="bg-white text-red-700 text-[10px] font-black px-2 py-1 rounded-full whitespace-nowrap">
                          YOUR WAREHOUSE
                        </span>
                      )}
                    </div>
                    <p className="text-red-100 mt-1 text-sm">{wh.address}</p>
                    <a href={wh.googleLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-white hover:text-yellow-200 text-xs font-semibold mt-2">
                      Open in Google Maps <ExternalLink className="w-3 h-3" />
                    </a>
                  </CardHeader>
                  <div className="h-80">
                    <iframe
                      src={wh.mapEmbed}
                      width="100%" height="100%"
                      style={{ border: 0 }} allowFullScreen="" loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade" title={`LCTX ${wh.id.toUpperCase()} warehouse`}
                    ></iframe>
                  </div>
                </Card>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}